// Issues Detected Modal — shown after a policy upload finishes scoring (WS "SCORED"
// status), listing the new feedbacks/gaps from that scoring run.
const classifyIssue = (item, kind) => {
  // A Dreams item is not a hole in the document — the policy may read fine. It
  // says the agents keep rewriting drafts on this point, so it is a suggestion
  // to reconcile, never a "Critical" missing field.
  if (item.source === "dreams") {
    return { badge: "From edits", dot: "#8B5CF6", bg: "#F5F3FF", badgeBg: "#EDE9FE", badgeColor: "#7C3AED" };
  }
  if (kind === "gap" && item.required && item.status === "missing") {
    return { badge: "Critical", dot: "#EF4444", bg: "#FFF5F5", badgeBg: "#FEE2E2", badgeColor: "#EF4444" };
  }
  if (item.status === "partial") {
    return { badge: "Warning", dot: "#F59E0B", bg: "#FFFBEB", badgeBg: "#FEF3C7", badgeColor: "#D97706" };
  }
  if (kind === "gap") {
    return { badge: "Warning", dot: "#F59E0B", bg: "#FFFBEB", badgeBg: "#FEF3C7", badgeColor: "#D97706" };
  }
  return { badge: "Suggestion", dot: "#3B82F6", bg: "#EFF6FF", badgeBg: "#DBEAFE", badgeColor: "#3B82F6" };
};

const IssuesDetectedModal = ({ isOpen, onClose, onReviewAndUpdate, topics = [], formatTopicKey = key => key }) => {
  if (!isOpen) return null;

  const issues = topics.flatMap(topic => [
    ...(topic.new_feedbacks ?? []).map(f => ({
      ...classifyIssue(f, "feedback"),
      // Backend item key — stable and unique per (channel, item), unlike the
      // label, which two topics can share.
      id: `${topic.topic_key}:${f.key}`,
      name: f.label,
      desc: f.conflict || f.evidence || f.question || `Needs review in ${formatTopicKey(topic.topic_key)}`,
      // Dreams learned this from real edits; the support count is what makes it
      // worth acting on, so show it rather than leaving the item unexplained.
      meta: f.occurrences ? `Seen in ${f.occurrences} draft${f.occurrences === 1 ? "" : "s"}` : "",
    })),
    ...(topic.new_gaps ?? []).map(g => ({
      ...classifyIssue(g, "gap"),
      id: `${topic.topic_key}:${g.key}`,
      name: g.label,
      desc: g.question || `Missing from ${formatTopicKey(topic.topic_key)}`,
      meta: "",
    })),
  ]);

  if (issues.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(12,13,23,0.28)] px-4 py-8 backdrop-blur-[2px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[700px] flex-col overflow-hidden rounded-[20px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(12,13,23,0.24)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="issues-detected-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="shrink-0 px-[40px] py-[24px] max-sm:px-5 max-sm:pb-4 max-sm:pt-5">
          <div className="flex min-w-0 items-start gap-4">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C8FFE7]">
              <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                <circle cx="11" cy="11" r="10" stroke="#1DB87B" strokeWidth="1.5" />
                <text
                  x="11"
                  y="16"
                  textAnchor="middle"
                  fill="#1DB87B"
                  fontSize="13"
                  fontWeight="bold"
                  fontFamily="DM Sans, sans-serif"
                >
                  i
                </text>
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p
                id="issues-detected-modal-title"
                className="font-dmsans text-[16px] font-[500] leading-[1.25] text-[#0C0D17] max-sm:text-[16px]"
              >
                Issues detected during analysis
              </p>
              <p className="mt-1.5 max-w-[540px] font-dmsans text-[13px] font-normal leading-[1.5] text-[#6B7280] max-sm:text-[12px]">
                We&apos;ve identified {issues.length} item{issues.length === 1 ? "" : "s"} that may affect template
                compatibility. You can apply the recommended updates now and continue the analysis.
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-[40px] pb-6 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#D7DBE4] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5 max-sm:max-h-[52vh] max-sm:px-5">
          <p className="mb-[16px] font-dmsans text-[12px] font-[600] uppercase tracking-[0.16em] text-[#A0A8B8]">
            Issues found
          </p>
          <div className="flex flex-col gap-3 pr-1">
            {issues.map(issue => (
              <div
                key={issue.id}
                className="flex min-h-[80px] items-start gap-3 rounded-[10px] px-4 py-3.5 transition-shadow max-sm:px-3.5"
                style={{ backgroundColor: issue.bg }}
              >
                <div className="pt-1.5">
                  <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: issue.dot }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="min-w-0 font-dmsans text-[13px] font-[600] leading-[1.35] text-[#111827] max-sm:text-[13px]">
                      {issue.name}
                    </span>
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 font-dmsans text-[10px] font-medium leading-none"
                      style={{
                        backgroundColor: issue.badgeBg,
                        color: issue.badgeColor,
                      }}
                    >
                      {issue.badge}
                    </span>
                  </div>
                  <p className="font-dmsans text-[12px] font-[400] leading-[1.45] text-[#667085] max-sm:text-[11px]">
                    {issue.desc}
                  </p>
                  {issue.meta ? (
                    <p className="mt-1 font-dmsans text-[11px] font-[400] leading-[1.4] text-[#98A2B3]">{issue.meta}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3 border-t border-[#EEF0F4] px-[40px] py-[24px] max-sm:px-5 max-sm:py-4">
          <button
            type="button"
            className="h-[46px] flex-1 rounded-[10px] border border-[#D9DEE8] bg-white font-dmsans text-[14px] font-medium text-[#4B5563] transition-colors hover:bg-[#F9FAFB]"
            onClick={onClose}
          >
            Review later
          </button>
          <button
            type="button"
            className="h-[46px] flex-1 rounded-[10px] bg-[#0C0D17] font-dmsans text-[14px] font-medium text-[#39F6A3] transition-colors hover:bg-[#1A1B2E]"
            onClick={onReviewAndUpdate}
          >
            Review and update
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssuesDetectedModal;
